import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import { Components, LoadingOverlay } from '@oneamerica/dxp-ui-components';
import responsiveStyle, { makeAPaymentButtonColors } from './style';
import InfoWrapper from '../../../../components/InfoWrapper/InfoWrapper';
import { LatestStatement } from '../../../../components/LatestStatements';
import { trackCustomEvent } from '../../../../utils/Analytics/AdobeAnalyticsUtils';
import { analyticsTags } from '../../../../utils/Analytics/AnalyticsTags';
import { useDispatch, useSelector } from 'react-redux';
import MakePaymentModal from './MakePaymentModal';
import { isHomeOfficeUser } from '../../../../utils';
import { PNC_SHO_URL } from '../../../../config/getEnvironmentVars';
import LegalDisclaimerModal from './LegalDisclaimerModal';
import { GET_PAYMENTS_DATA } from '../../../../redux/payments/types';
import { resetPaymentsEncryptedData } from '../../../../redux/payments/paymentsSlice';
import { ProcessingTroubleModal } from '../../../../components/ProcessingTroubleModal';

// Constants
const PAYMENT_CODES = {
  BASE_PREMIUM: 'P',
  PAID_UP_ADDITION: 'U',
  LOAN_PAYMENT: 'L',
  LOAN_INTEREST: 'I',
  LOAN_PAYOFF: 'O',
} as const;

const PREMIUM_PAYMENT_CODES = new Set(['P', 'U']);
const LOAN_PAYMENT_CODES = new Set(['L', 'I', 'O']);

// Helper functions
const getPolicySessionKey = (policyNumber: string) =>
  `paymentStatus_${policyNumber}`;
const getHandledKey = (policyNumber: string) =>
  `paymentStatusHandled_${policyNumber}`;
const getPaymentTypeKey = (policyNumber: string) =>
  `paymentType_${policyNumber}`;

const BillingAndPremium = ({
  billingAndPremiumData,
  accessToken,
  policyNumber,
  account,
}) => {
  const style = responsiveStyle();
  const dispatch = useDispatch();

  const [isModalVisible, setModalVisible] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [paymentData, setPaymentData] = useState<any>([]);
  const [selectedPaymentCodes, setSelectedPaymentCodes] = useState<string[]>(
    [],
  );

  const policyData = useSelector((state: any) => state.policies);
  const paymentEncryptedData = useSelector(
    (state: any) => state.paymentEncryptedData,
  );

  // Extract policy highlights for easier access
  const policyHighlight =
    policyData?.getPoliciesListData?.data?.policySummary?.policyHighlights?.[0];
  const accountConfig = policyHighlight?.accountConfig;
  const generalPolicyData = policyHighlight?.generalPolicyData;

  // Selectors
  const loansData = policyHighlight?.policyLoan;
  const hasPendingPremiumPayment = accountConfig?.hasPendingPremiumPayment;
  const hasPendingLoanPayment = accountConfig?.hasPendingLoanPayment;
  const allowLoanPayment = accountConfig?.allowLoanPayment;
  const allowPremiumPayment = accountConfig?.allowPremiumPayment;
  const allowLoanInterest = accountConfig?.allowLoanInterest;
  const allowLatestStatement = accountConfig?.allowLatestStatement;
  const apiAccountNumber =
    policyData?.getPoliciesListData?.data?.policySummary?.account?.number;

  const isHomeOfcUser = isHomeOfficeUser();

  const showMakePaymentButton = useMemo(() => {
    return (
      allowPremiumPayment === true ||
      allowLoanPayment === true ||
      allowLoanInterest === true
    );
  }, [allowPremiumPayment, allowLoanPayment, allowLoanInterest]);

  const formRef = useRef<HTMLFormElement | null>(null);
  const redirectShownRef = useRef(false);
  const hasBothPendingPaymentFlags =
    hasPendingPremiumPayment === true && hasPendingLoanPayment === true;
  const shouldShowSuccessBanner = paymentStatus === 'success';

  // Handle payment status from URL and API
  const handlePaymentStatus = () => {
    const currentPolicyNumber = account?.number;
    const apiHasPending = hasPendingPremiumPayment || hasPendingLoanPayment;
    const key = getPolicySessionKey(currentPolicyNumber);
    const storedStatus = sessionStorage.getItem(key);
    const urlParams = new URLSearchParams(globalThis.location.search);
    const initialRedirectIsSuccess =
      urlParams.get('paymentStatus') === 'success';
    const handledKey = getHandledKey(currentPolicyNumber);
    const handled = sessionStorage.getItem(handledKey) === 'true';
    const isApiPendingForThisPolicy =
      Boolean(apiHasPending) && apiAccountNumber === currentPolicyNumber;

    if (initialRedirectIsSuccess && !handled && currentPolicyNumber) {
      redirectShownRef.current = true;
      setPaymentStatus('success');
      return;
    }

    if (isApiPendingForThisPolicy) {
      if (storedStatus === 'success' && redirectShownRef.current === true) {
        setPaymentStatus('success');
        return;
      }
      setPaymentStatus('pending');
      return;
    }

    if (storedStatus === 'success') {
      setPaymentStatus('success');
    } else {
      setPaymentStatus(undefined);
    }
  };

  useEffect(() => {
    handlePaymentStatus();
  }, [account?.number, policyData]);

  const getPaymentTypes = () => {
    const payments: { title: string; amount: number }[] = [
      {
        title: 'Base Premium',
        amount: generalPolicyData?.billingAmount || 0,
      },
      {
        title: 'Paid Up Addition',
        amount: generalPolicyData?.billRiderAmt || 0,
      },
    ];

    return payments.filter((item) => item.amount > 0);
  };

  useEffect(() => {
    dispatch(resetPaymentsEncryptedData());
    setPaymentData(null);
    setIsLoading(false);
  }, [dispatch]);

  const handlePaymentEncryptedDataChange = () => {
    if (paymentEncryptedData?.data || paymentEncryptedData?.error) {
      setIsLoading(false);
    }
    if (paymentEncryptedData?.data) {
      setPaymentData(paymentEncryptedData.data);
      return;
    }
    if (paymentEncryptedData?.error) {
      trackCustomEvent(
        analyticsTags.CapturePaymentDataApiFailurePolicyDetailsPage,
      );
      console.error('Error fetching payment data:', paymentEncryptedData.error);
      setPaymentData(null);
    }
  };

  useEffect(() => {
    handlePaymentEncryptedDataChange();
  }, [paymentEncryptedData]);

  const onPressMakeAPayment = () => {
    setIsLoading(true);
    trackCustomEvent(analyticsTags.CaptureMakeAPaymentButtonPolicDetailsTags);
    setModalVisible(true);
    setIsLoading(false);
  };

  const trackPaymentTypeAnalytics = (paymentCodes: string[]) => {
    const hasPremiumPayment = paymentCodes.some((code) =>
      PREMIUM_PAYMENT_CODES.has(code),
    );
    const hasLoanPayment = paymentCodes.some((code) =>
      LOAN_PAYMENT_CODES.has(code),
    );

    if (hasPremiumPayment && hasLoanPayment) {
      trackCustomEvent(analyticsTags.CapturePortalPremiumAndLoanPayment);
    } else if (hasPremiumPayment) {
      trackCustomEvent(analyticsTags.CapturePortalPremiumPayment);
    } else if (hasLoanPayment) {
      trackCustomEvent(analyticsTags.CapturePortalLoanPayment);
    }
  };

  const handleLegalDisclaimerAgree = () => {
    if (sessionStorage.getItem('pncCallbackRole')) {
      sessionStorage.removeItem('pncCallbackRole');
    }

    trackPaymentTypeAnalytics(selectedPaymentCodes);

    if (formRef.current) {
      formRef.current.submit();
    }
  };

  const sendAnalytics = () => {
    trackCustomEvent(
      analyticsTags.CaptureMakeAPaymentCloseButtonPolicyDetailsPageTags,
    );
  };

  const determinePaymentType = (selectedPayments): string => {
    const hasPremium = selectedPayments.some(
      (p) =>
        p.title?.toLowerCase() === 'base premium' ||
        p.title?.toLowerCase() === 'paid up addition',
    );
    const hasLoan = selectedPayments.some((p) =>
      ['loan payment', 'loan interest', 'loan payoff'].includes(
        p.title?.toLowerCase(),
      ),
    );

    if (hasPremium && hasLoan) {
      return 'Both';
    }
    if (hasLoan) {
      return 'Loan';
    }
    return 'Premium';
  };

  const getSelectedPaymentsCodes = (selectedPayments): string[] => {
    const paymentMap = {
      'base premium': PAYMENT_CODES.BASE_PREMIUM,
      'paid up addition': PAYMENT_CODES.PAID_UP_ADDITION,
      'loan payment': PAYMENT_CODES.LOAN_PAYMENT,
      'loan interest': PAYMENT_CODES.LOAN_INTEREST,
      'loan payoff': PAYMENT_CODES.LOAN_PAYOFF,
    };

    const codes: string[] = [];
    selectedPayments.forEach((payment) => {
      const title = payment.title?.toLowerCase();
      if (title && paymentMap[title]) {
        codes.push(paymentMap[title]);
      }
    });

    return codes;
  };
  const disableMakePaymentButton = (): boolean => {
    if (
      isLoading ||
      isHomeOfcUser ||
      (shouldShowSuccessBanner && hasBothPendingPaymentFlags)
    ) {
      return true;
    }

    if (hasPendingPremiumPayment === true && hasPendingLoanPayment === true) {
      return true;
    }

    if (
      !allowPremiumPayment &&
      (allowLoanPayment === true || allowLoanInterest === true) &&
      hasPendingLoanPayment === true
    ) {
      return true;
    }

    if (
      allowPremiumPayment === true &&
      hasPendingPremiumPayment === true &&
      !allowLoanPayment &&
      !allowLoanInterest
    ) {
      return true;
    }

    return false;
  };

  const handleMakePaymentSubmit = (selectedPayments) => {
    setIsLoading(true);
    trackCustomEvent(
      analyticsTags.CaptureMakeAPaymentSubmitButtonPolicyDetailsPageTags,
    );

    const paymentCodes = getSelectedPaymentsCodes(selectedPayments) || [];
    setSelectedPaymentCodes(paymentCodes);

    const loanPayment = selectedPayments.find(
      (p) => p.title?.toLowerCase() === 'loan payment',
    );
    const loanInterest = selectedPayments.find(
      (p) => p.title?.toLowerCase() === 'loan interest',
    );

    const selectedPaymentType = determinePaymentType(selectedPayments);
    const currentPolicyNumber = apiAccountNumber;

    if (currentPolicyNumber) {
      sessionStorage.setItem(
        getPaymentTypeKey(currentPolicyNumber),
        selectedPaymentType,
      );
    }

    dispatch({
      type: GET_PAYMENTS_DATA,
      payload: {
        token: policyData?.token,
        policyNumber: currentPolicyNumber,
        paymentTypes: paymentCodes,
        loanAmount: loanPayment?.amount,
        loanInterestAmount: loanInterest?.amount,
        isLoading: true,
      },
    });

    setModalVisible(false);
  };

  return (
    <>
      <LoadingOverlay loading={isLoading} />
      <View className="lg:flex-row">
        <View className="w-full p-0 mt-8">
          {showMakePaymentButton && (
            <View style={{ alignItems: 'flex-end', marginBottom: 18 }}>
              <>
                <Components.Button
                  className="mx-2"
                  accessibilityRole="button"
                  id="btnOutline"
                  theme="primary"
                  {...makeAPaymentButtonColors}
                  disabled={disableMakePaymentButton()}
                  onPress={() => {
                    onPressMakeAPayment();
                  }}
                  style={style.makeaPaymentButton}
                >
                  Make a Payment
                </Components.Button>
                <form
                  ref={formRef}
                  action={PNC_SHO_URL}
                  method="post"
                  style={{ display: 'none' }}
                >
                  <input
                    type="hidden"
                    name="BillTypeLabel"
                    value={paymentData?.billTypeLabel ?? ''}
                  />
                  <input
                    type="hidden"
                    name="CompanyName"
                    value={paymentData?.companyName ?? ''}
                  />
                  <input
                    type="hidden"
                    name="AmountDueLabel"
                    value={paymentData?.amountDueLabel ?? ''}
                  />
                  <input
                    type="hidden"
                    name="EmailAddress"
                    value={paymentData?.emailAddress ?? ''}
                  />
                  <input
                    type="hidden"
                    name="FDICode"
                    value={paymentData?.fdiCode ?? ''}
                  />
                  <input
                    type="hidden"
                    name="PolicyNumber"
                    value={paymentData?.policyNumber ?? ''}
                  />
                  <input
                    type="hidden"
                    name="ClientID"
                    value={paymentData?.clientID ?? ''}
                  />
                  <input
                    type="hidden"
                    name="ID"
                    value={paymentData?.ID ?? ''}
                  />
                </form>
              </>
            </View>
          )}
          <View style={style.cardShadow}>
            <View
              style={[
                style.cardHeader,
                {
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  alignItems: 'flex-start',
                },
              ]}
            >
              <Components.Text.H3 style={[style.cardHeaderTitle, style.flex]}>
                Billing & Premiums
              </Components.Text.H3>
            </View>
            <View className="mt-4" style={style.horizontalDivider} />
            <View
              style={{
                flex: 1,
                flexDirection: 'row',
                flexWrap: 'wrap',
              }}
              className="mt-4 w-full"
            >
              {billingAndPremiumData.map((data, index) => {
                return (
                  <View
                    key={`${data.title}-${index}`}
                    style={{
                      paddingBottom: 20,
                    }}
                    className="w-1/2 sm:w-1/2 md:w-1/3 lg:w-1/4"
                  >
                    <InfoWrapper
                      title={data.title}
                      subTitle
                      information={data.value ?? '-'}
                      infoTextStyle={{
                        fontWeight: '500',
                      }}
                    />
                  </View>
                );
              })}
            </View>
            <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
              {allowLatestStatement && (
                <LatestStatement
                  onPress={() => {
                    trackCustomEvent(
                      analyticsTags.CaptureLatestStatementspolicyDetailspg,
                    );
                  }}
                  accessToken={accessToken}
                  policyNumber={policyNumber}
                  pageName={'POLICY FINANCIALS'}
                />
              )}
            </View>
          </View>
        </View>
      </View>
      <MakePaymentModal
        onSubmit={handleMakePaymentSubmit}
        onCloseAnalytics={sendAnalytics}
        isModalVisible={isModalVisible}
        setModalVisible={setModalVisible}
        payments={getPaymentTypes()}
        allowLoanPayment={allowLoanPayment}
        allowLoanInterest={allowLoanInterest}
        loanPayoffAmount={loansData?.loanPayOff || 0}
        loanBalance={loansData?.loanBalance || 0}
        allowPremiumPayment={allowPremiumPayment}
        hasPendingPremiumPayment={hasPendingPremiumPayment}
        hasPendingLoanPayment={hasPendingLoanPayment}
      />
      <LegalDisclaimerModal
        isVisible={!!paymentEncryptedData?.data}
        onAgree={handleLegalDisclaimerAgree}
        isHomePage={false}
        onClose={() => {
          dispatch(resetPaymentsEncryptedData());
          setPaymentData(null);
          setIsLoading(false);
        }}
      />
      <ProcessingTroubleModal
        showModal={!!paymentEncryptedData?.error}
        onClose={() => {
          dispatch(resetPaymentsEncryptedData());
          setIsLoading(false);
          trackCustomEvent(
            analyticsTags.CaptureProcessingTroubleModalClosePolicyDetailsPage,
          );
        }}
      />
    </>
  );
};

export default BillingAndPremium;
